import { Badge } from '@/ui-components/atoms/Badge/Badge';
import { Button, type ButtonProps } from '@/ui-components/atoms/Button/Button';
import styles from './header.module.scss';
import { Text } from '@/ui-components/atoms/Text/Text';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import { HeaderProps } from './Header';
import { ColBreakpoints } from '@/ui-components/atoms/Layout/Grid';
import cn from 'classnames';
import { CTAGroup } from '@/ui-components/molecules/CTAGroup/CTAGroup';

export const HeaderCenterOrLeft = ({
    badge,
    eyebrow,
    heading,
    headingTag,
    subhead,
    channelNumber,
    caption,
    links,
    ctaGroupAlignment = 'left',
    theme = 'center',
    inheritTextColor = false,
    subHeadAbove = false,
    className,
    headingAriaLabel,
    type,
    headingAlignment,
    headingColumns,
    alwaysLeftMobileText = false,
    mobileTextAlignment,
}: HeaderProps): React.JSX.Element => {
    const setThemeCols = (cols: ColBreakpoints): ColBreakpoints =>
        theme === 'full-width-center' ? { sm: 12 } : theme === 'left' ? {} : cols;

    const headingGridProps: { col: ColBreakpoints; start: ColBreakpoints } | null =
        headingAlignment === 'left'
            ? headingColumns === '10 Columns'
                ? { col: { sm: 12, md: 8, lg: 8 }, start: { md: 2, lg: 2 } }
                : headingColumns === '8 Columns'
                  ? { col: { sm: 12, md: 8, lg: 8 }, start: { md: 3, lg: 3 } }
                  : headingColumns === '12 Columns'
                    ? { col: { sm: 12, md: 8, lg: 8 }, start: {} }
                    : { col: {}, start: {} }
            : null;

    const inPromoBannerCard = type === 'promoBannerCard';
    const containerClasses = cn(styles[theme], styles.container, className, {
        [styles.headingElementSpacing]: inPromoBannerCard,
        [styles.alignLeft]: headingAlignment === 'left',
        [styles.alignCenter]: headingAlignment === 'center',
    });

    const badgeStyles = cn(styles.badgeContainer, {
        [styles.inCardStyles]: inPromoBannerCard,
    });

    const headingStyles = cn(headingTag || 'h2', styles.heading, {
        [styles.headingInPromoBannerCard]: inPromoBannerCard,
        [styles.headingInHeroHeader]: type === 'heroHeader' && theme === 'left' && !alwaysLeftMobileText,
        [styles.headingInHeroHeaderAlwaysLeft]: type === 'heroHeader' && alwaysLeftMobileText,
    });

    const subheadStyles = cn(inheritTextColor ? styles.subhead : styles.subheadDefault, {
        [styles.subheadInPromoBannerCard]: inPromoBannerCard,
        [styles.subheadInHeroHeader]: type === 'heroHeader',
        [styles.subheadInHeroHeaderLeft]: type === 'heroHeader' && theme === 'left' && !alwaysLeftMobileText,
        [styles.subheadInHeroHeaderAlwaysLeft]: type === 'heroHeader' && (theme === 'always-left' || alwaysLeftMobileText),
    });

    const buttonContainerStyles = cn(styles.buttonContainer, {
        [styles.leftAlignedButton]: theme === 'left',
    });

    const linkNodes: React.ReactElement<ButtonProps>[] = [];

    if (links && links.length > 0) {
        const mainLinks = links.filter((link) => (link as ButtonProps).variant !== 'tertiary');
        const hasTwoMain = mainLinks.length === 2;
        const isPromoBanner = type === 'promoBanner';

        for (let i = 0; i < 3; i += 1) {
            const link = links[i] as ButtonProps | undefined;
            if (!link) continue;

            const variant = link.variant;
            const fitting: ButtonProps['fitting'] = isPromoBanner
                ? 'fill-container'
                : variant === 'tertiary'
                  ? 'hug-contents'
                  : hasTwoMain
                    ? 'fill-container'
                    : 'hug-contents';

            linkNodes.push(<Button key={i} {...link} fitting={fitting} className={styles.button} />);
        }
    }

    return (
        <Grid col={{ sm: 12 }} className={containerClasses} container noPadding>
            <Grid col={{ sm: 12 }} className={cn(styles.headingContainer, { [styles.headingContainerMobileLeft]: mobileTextAlignment === 'Left' }, 'heading')} container>
                {eyebrow && (
                    <Grid
                        col={headingGridProps ? headingGridProps.col : setThemeCols({ sm: 12, md: 8, lg: 8 })}
                        start={headingGridProps ? headingGridProps.start : setThemeCols({ md: 3, lg: 3 })}
                    >
                        <Text tag="div" className={styles.eyebrow}>
                            {eyebrow}
                        </Text>
                    </Grid>
                )}
                {subHeadAbove ? (
                    <>
                        {subhead && (
                            <Grid col={headingGridProps ? headingGridProps.col : setThemeCols({ sm: 12, md: 6, lg: 6 })} start={headingGridProps ? headingGridProps.start : setThemeCols({ lg: 4, md: 4 })}>
                                <Text
                                    tag="div"
                                    variant="text-xlarge"
                                    className={`${inheritTextColor ? styles.subhead : styles.subheadDefault} ${styles.subHeadAbove} ${type === 'heroHeader' ? styles.subheadInHeroHeader : ''}`}
                                >
                                    {channelNumber ? (
                                        <>
                                            CH {channelNumber}
                                            <br />
                                            {subhead}
                                        </>
                                    ) : (
                                        subhead
                                    )}
                                </Text>
                            </Grid>
                        )}
                        {heading && (
                            <Grid col={headingGridProps ? headingGridProps.col : setThemeCols({ sm: 12, md: 8, lg: 8 })} start={headingGridProps ? headingGridProps.start : setThemeCols({ md: 3, lg: 3 })}>
                                {badge && (
                                    <div className={badgeStyles}>
                                        <Badge {...badge} />
                                    </div>
                                )}
                                <Text ariaLabel={headingAriaLabel} tag={headingTag || 'h2'} className={headingStyles}>
                                    {heading}
                                </Text>
                            </Grid>
                        )}
                    </>
                ) : (
                    <>
                        {heading && (
                            <Grid col={headingGridProps ? headingGridProps.col : setThemeCols({ sm: 12, md: 8, lg: 8 })} start={headingGridProps ? headingGridProps.start : setThemeCols({ md: 3, lg: 3 })}>
                                {badge && (
                                    <div className={badgeStyles}>
                                        <Badge {...badge} />
                                    </div>
                                )}
                                <Text ariaLabel={headingAriaLabel} tag={headingTag || 'h2'} className={headingStyles}>
                                    {heading}
                                </Text>
                            </Grid>
                        )}

                        {subhead && (
                            <Grid col={headingGridProps ? headingGridProps.col : setThemeCols({ sm: 12, md: 6, lg: 6 })} start={headingGridProps ? headingGridProps.start : setThemeCols({ lg: 4, md: 4 })}>
                                <Text tag="div" variant="text-xlarge" className={subheadStyles}>
                                    {channelNumber ? (
                                        <>
                                            CH {channelNumber}
                                            <br />
                                            {subhead}
                                        </>
                                    ) : (
                                        subhead
                                    )}
                                </Text>
                            </Grid>
                        )}
                    </>
                )}
            </Grid>

            {!!links?.length && (
                <Grid
                    className={buttonContainerStyles}
                    col={headingGridProps ? headingGridProps.col : undefined}
                    start={headingGridProps ? headingGridProps.start : undefined}
                >
                    <CTAGroup
                        links={linkNodes}
                        caption={caption}
                        desktopAlign={headingAlignment ?? ctaGroupAlignment}
                        mobileAlign={headingAlignment === 'left' ? 'left' : 'center'}
                    />
                </Grid>
            )}
        </Grid>
    );
};
